export const initialPromotion = {
  name: "",
  code: "",
  description: "",
  status: "draft",
  mode: "automatic",
  priority: "1",
  icon: "",
  badge: "",
  storeIds: [],
  applicableCities: [],
  applicableDeliveryZones: [],
  startDate: "",
  endDate: "",
  expiryDate: "",
  qualification: {
    type: "spendAmount",
    minimumOrder: "",
    quantity: "",
    productIds: [],
    categoryIds: [],
    brandIds: [],
    comboItems: [],
    entities: [],
    customRule: "",
    repeatMode: "NONE",
    repeatLimit: "",
  },
  reward: {
    type: "flatDiscount",
    amount: "",
    percent: "",
    maximumDiscount: "",
    targetPrice: "",
    productIds: [],
    quantity: "",
    points: "",
    walletCredit: "",
    // Dynamic reward target fields
    targetType: "product",      // product | category | brand | combo
    rewardCategory: "",
    rewardVariantId: "",
    rewardBrand: "",
    rewardCombo: [],
    entities: [],
  },
  policy: {
    selection: "fixedProduct",
    maxRewardQuantity: "",
    rewardRepeatMode: "UNLIMITED",
    rewardLimit: "",
  },
  restrictions: {
    firstOrderOnly: false,
    specificCustomers: [],
    customerGroups: [],
    storeIds: [],
    deliveryTypes: [],
    maximumUsage: "",
    maximumUsagePerCustomer: "",
    allowStacking: true,
  },
};

const firstNumber = (value) => {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeRepeatMode = (value, defaultValue) => {
  if (typeof value !== "string") return defaultValue;
  const normalized = value.trim().toUpperCase();
  if (normalized === "LIMITED" || normalized === "LIMIT") return "LIMITED";
  if (normalized === "NONE") return "NONE";
  if (normalized === "UNLIMITED") return "UNLIMITED";
  return defaultValue;
};

const arrayify = (value) => {
  if (Array.isArray(value)) {
    return value
      .filter(Boolean)
      .map((item) => {
        if (typeof item === "string") return item.trim();
        if (typeof item === "object" && item !== null) {
          return item.name || item.id || item._id || JSON.stringify(item);
        }
        return String(item).trim();
      })
      .filter(Boolean);
  }

  if (typeof value === "string" && value.trim()) {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof value === "object" && value !== null) {
    return [String(value).trim()].filter(Boolean);
  }

  return [];
};

const parseLegacyItems = (items) => {
  if (!items) return [];
  const list = Array.isArray(items) ? items : [items];
  return list.map(item => {
    if (item && typeof item === "object") {
      return {
        productId: item.productId || "",
        variantId: item.variantId || "any"
      };
    }
    return {
      productId: String(item).trim(),
      variantId: "any"
    };
  }).filter(item => item.productId);
};

export const mapPromotionToLegacyPayload = (promotion) => {
  const qualEntities = promotion.qualification.entities || [];
  const rewardEntities = promotion.reward.entities || [];

  // Update legacy arrays based on qualification type
  let buyItems = [];
  let categoryIds = [];
  let categoryItems = [];
  let comboItems = [];
  let buyQuantity = promotion.qualification.quantity;

  if (promotion.qualification.type === "buyProduct") {
    buyItems = qualEntities.map(e => ({ productId: e.entityId, variantId: e.variantId }));
    buyQuantity = qualEntities.reduce((sum, e) => sum + (Number(e.quantity) || 1), 0);
  } else if (promotion.qualification.type === "buyCategory") {
    categoryIds = qualEntities.map(e => e.entityId);
    categoryItems = qualEntities.map(e => ({
      categoryId: e.entityId,
      variantId: e.variantId,
      quantity: Number(e.quantity) || 1
    }));
    buyQuantity = qualEntities.reduce((sum, e) => sum + (Number(e.quantity) || 1), 0);
  } else if (promotion.qualification.type === "buyCombo") {
    comboItems = qualEntities.map(e => ({
      productId: e.entityId,
      variantId: e.variantId,
      quantity: Number(e.quantity) || 1
    }));
    buyQuantity = 1;
  }

  // Update legacy arrays based on reward targetType
  let freeItems = [];
  let rewardCategory = "";
  let rewardVariantId = "";
  let rewardCombo = [];
  let rewardQuantity = promotion.reward.quantity;

  if (promotion.reward.type === "freeProduct") {
    if (promotion.reward.targetType === "product") {
      freeItems = rewardEntities.map(e => ({ productId: e.entityId, variantId: e.variantId }));
      rewardQuantity = rewardEntities.reduce((sum, e) => sum + (Number(e.quantity) || 1), 0);
    } else if (promotion.reward.targetType === "category") {
      if (rewardEntities.length > 0) {
        rewardCategory = rewardEntities[0].entityId;
        rewardVariantId = rewardEntities[0].variantId;
        rewardQuantity = Number(rewardEntities[0].quantity) || 1;
      }
    } else if (promotion.reward.targetType === "combo") {
      rewardCombo = rewardEntities.map(e => ({
        productId: e.entityId,
        variantId: e.variantId,
        quantity: Number(e.quantity) || 1
      }));
      rewardQuantity = 1;
    }
  }

  const isFreeProduct = promotion.reward.type === "freeProduct";
  const mappedPolicy = {
    selection: isFreeProduct ? promotion.policy.selection : null,
    maxRewardQuantity: isFreeProduct ? (firstNumber(promotion.policy.maxRewardQuantity) || 1) : null,
    rewardRepeatMode: isFreeProduct ? promotion.policy.rewardRepeatMode : "NONE",
    rewardLimit: isFreeProduct ? firstNumber(promotion.policy.rewardLimit) : null,
  };

  return {
    name: promotion.name,
    code: promotion.code,
    description: promotion.description,
    status: promotion.status,
    mode: promotion.mode,
    priority: Number(promotion.priority) || 0,
    icon: promotion.icon || "",
    badge: promotion.badge || "",
    storeIds: promotion.storeIds,
    applicableCities: promotion.applicableCities,
    applicableDeliveryZones: promotion.applicableDeliveryZones,
    startDate: promotion.startDate,
    endDate: promotion.endDate,
    expiryDate: promotion.expiryDate || promotion.endDate,
    qualification: {
      ...promotion.qualification,
      buyQuantity,
      buyItems,
      categoryIds,
      categoryItems,
      comboItems,
      entities: qualEntities
    },
    reward: {
      ...promotion.reward,
      // Normalize targetType to UPPERCASE for the server
      targetType: (promotion.reward.targetType || "product").toUpperCase(),
      rewardQuantity,
      rewardItems: freeItems,
      rewardCategory,
      rewardVariantId,
      rewardCombo,
      entities: rewardEntities
    },
    policy: mappedPolicy,
    restrictions: promotion.restrictions,
    buyItems: buyItems.map(item => item.productId),
    freeItems: freeItems.map(item => item.productId),
    discountPercent: firstNumber(promotion.reward.type === "percentageDiscount" ? promotion.reward.percent : promotion.reward.maximumDiscount),
    discountAmount: firstNumber(promotion.reward.type === "flatDiscount" ? promotion.reward.amount : promotion.reward.type === "comboPrice" ? promotion.reward.amount : undefined),
    minimumOrder: firstNumber(promotion.qualification.minimumOrder),
    maximumDiscount: firstNumber(promotion.reward.maximumDiscount),
    xValue: firstNumber(buyQuantity),
    yValue: firstNumber(rewardQuantity),
    usage: promotion.qualification.repeatMode === "NONE" ? "single" : "multi",
    rewardRepeatMode: isFreeProduct ? promotion.policy.rewardRepeatMode : "NONE",
    rewardLimit: isFreeProduct ? firstNumber(promotion.policy.rewardLimit) : null,
  };
};

const normalizeLegacyStatus = (status) => {
  if (!status) return "draft";
  const normalized = String(status).toLowerCase();
  if (normalized === "enable" || normalized === "active") return "active";
  if (normalized === "disable" || normalized === "disabled") return "disabled";
  return "draft";
};

/**
 * Derives the frontend qualification type from a Firestore promotion document.
 * The DB stores types in UPPERCASE (e.g. "SPENDAMOUNT", "BUYPRODUCT") written by mapPayload.
 * Falls back to legacy root-level discountType for old documents.
 */
const deriveLegacyQualificationType = (coupon) => {
  const nestedType = String(coupon.qualification?.type || "").toUpperCase().replace(/_/g, "");
  if (nestedType === "SPENDAMOUNT") return "spendAmount";
  if (nestedType === "BUYPRODUCT") return "buyProduct";
  if (nestedType === "BUYCATEGORY") return "buyCategory";
  if (nestedType === "BUYBRAND") return "buyBrand";
  if (nestedType === "BUYCOMBO" || nestedType === "COMBO") return "buyCombo";
  if (nestedType === "FIRSTORDER") return "firstOrder";
  if (nestedType === "DELIVERYMODE" || nestedType === "FREEDEILVERY") return "freeDelivery";
  if (nestedType === "FLATDISCOUNT") return "flatDiscount";
  if (nestedType === "PERCENTAGEDISCOUNT") return "percentageDiscount";
  if (nestedType === "CUSTOM") return "custom";

  // Fallback to legacy root level fields
  const type = String(coupon.discountType || "").toLowerCase();
  if (coupon.buyItems?.length) return "buyProduct";
  if (type === "buyxgety" || type === "buyxget" || type === "buyxgetyfree") return "buyProduct";
  if (type === "freedelivery") return "freeDelivery";
  if (type === "buycombo" || type === "combo") return "buyCombo";
  if (type === "firstorder" || type === "first_order") return "firstOrder";
  if (type === "spendget" || type === "spend_amount" || type === "spendamount") return "spendAmount";
  return "spendAmount";
};

/**
 * Derives the frontend reward type from a Firestore promotion document.
 * The DB stores types in UPPERCASE (e.g. "FREE_ITEM", "FLATDISCOUNT") written by mapPayload.
 * Covers all variants saved by mapPayload.
 */
const deriveLegacyRewardType = (coupon) => {
  // Check nested reward.type first — strip underscores and uppercase for loose matching
  const nestedType = String(coupon.reward?.type || "").toUpperCase().replace(/_/g, "");
  if (nestedType === "FREEITEM" || nestedType === "FREEPRODUCT") return "freeProduct";
  if (nestedType === "PERCENTAGE" || nestedType === "PERCENT" || nestedType === "PERCENTAGEDISCOUNT") return "percentageDiscount";
  if (nestedType === "FIXED" || nestedType === "FIXEDAMOUNT" || nestedType === "FLAT" || nestedType === "FLATDISCOUNT") return "flatDiscount";
  if (nestedType === "FREEDELIVERY" || nestedType === "FREEDEL") return "freeDelivery";
  if (nestedType === "COMBO" || nestedType === "COMBOPRICE") return "comboPrice";
  if (nestedType === "FIXEDPRICE") return "fixedPrice";
  if (nestedType === "POINTS" || nestedType === "REWARDPOINTS") return "rewardPoints";
  if (nestedType === "WALLET" || nestedType === "WALLETCREDIT") return "walletCredit";

  // Fallback to legacy root level discountType
  const type = String(coupon.discountType || "").toLowerCase();
  const freeItems = arrayify(coupon.freeItems || coupon.reward?.rewardItems);
  const hasBuyGetType = type.includes("buy") && type.includes("get");
  const hasYValue = Number(coupon.yValue ?? coupon.reward?.rewardQuantity) > 0;

  if (freeItems.length || hasBuyGetType || hasYValue) return "freeProduct";
  if (type === "percent" || type === "percentage") return "percentageDiscount";
  if (type === "fixed" || type === "flat") return "flatDiscount";
  if (type === "freedelivery") return "freeDelivery";
  if (type === "comboprice" || type === "combo") return "comboPrice";
  if (type === "fixedprice") return "fixedPrice";
  if (type === "rewardpoints") return "rewardPoints";
  if (type === "walletcredit") return "walletCredit";
  return "flatDiscount";
};

/**
 * Maps a raw Firestore promotion document (as returned by getAll / getById)
 * to the admin form's internal state shape.
 *
 * Priority for every field:
 *   1. Nested structured fields (written by mapPayload, the new canonical location)
 *   2. Root legacy fields (older documents or docs written by the old API)
 *   3. Safe empty defaults
 */
export const mapLegacyCouponToPromotion = (coupon = {}) => {
  const q = coupon.qualification || {};
  const r = coupon.reward || {};
  const pol = coupon.policy || {};
  const res = coupon.restrictions || {};

  const qType = deriveLegacyQualificationType(coupon);
  const rType = deriveLegacyRewardType(coupon);

  // 1. Parse or Synthesize qualification entities
  let qualEntities = q.entities || [];
  if (!qualEntities.length) {
    const rootQty = Number(q.buyQuantity ?? q.quantity ?? coupon.xValue) || 1;
    if (qType === "buyProduct") {
      const items = parseLegacyItems(q.buyItems || q.productIds || coupon.buyItems);
      qualEntities = items.map(item => ({
        type: "PRODUCT",
        entityId: item.productId,
        variantId: item.variantId || "any",
        quantity: rootQty
      }));
    } else if (qType === "buyCategory") {
      if (Array.isArray(q.categoryItems) && q.categoryItems.length > 0) {
        qualEntities = q.categoryItems.map(item => ({
          type: "CATEGORY",
          entityId: item.categoryId || item.entityId,
          variantId: item.variantId || "any",
          quantity: Number(item.quantity) || rootQty
        }));
      } else {
        const cats = arrayify(q.categoryIds || coupon.categoryIds);
        if (q.buyCategory) cats.push(q.buyCategory);
        if (coupon.buyCategory) cats.push(coupon.buyCategory);
        const uniqueCats = Array.from(new Set(cats));
        qualEntities = uniqueCats.map(catId => ({
          type: "CATEGORY",
          entityId: catId,
          variantId: q.variantId || "any",
          quantity: rootQty
        }));
      }
    } else if (qType === "buyCombo") {
      const items = q.comboItems || coupon.comboItems || [];
      qualEntities = items.map(item => ({
        type: "PRODUCT",
        entityId: item.productId,
        variantId: item.variantId || "any",
        quantity: Number(item.quantity) || 1
      }));
    }
  }

  // 2. Parse or Synthesize reward entities
  let rewardEntities = r.entities || [];
  if (!rewardEntities.length && rType === "freeProduct") {
    const targetType = r.targetType ? r.targetType.toLowerCase() : "product";
    const rewardQty = Number(r.rewardQuantity ?? r.quantity ?? coupon.yValue) || 1;
    if (targetType === "product") {
      const items = parseLegacyItems(r.rewardItems || r.productIds || coupon.freeItems);
      rewardEntities = items.map(item => ({
        type: "PRODUCT",
        entityId: item.productId,
        variantId: item.variantId || "any",
        quantity: rewardQty
      }));
    } else if (targetType === "category") {
      if (r.rewardCategory) {
        rewardEntities = [{
          type: "CATEGORY",
          entityId: r.rewardCategory,
          variantId: r.rewardVariantId || r.variantId || "any",
          quantity: rewardQty
        }];
      }
    } else if (targetType === "combo") {
      const items = r.rewardCombo || [];
      rewardEntities = items.map(item => ({
        type: "PRODUCT",
        entityId: item.productId,
        variantId: item.variantId || "any",
        quantity: Number(item.quantity) || 1
      }));
    }
  }

  const promotion = {
    name: coupon.heading || coupon.name || "",
    code: coupon.code || coupon.couponCode || "",
    description: coupon.description || "",
    status: normalizeLegacyStatus(coupon.status),

    // Bug 5 fix: prefer stored 'mode' field; only use the old heuristic as last resort
    mode: coupon.mode || (coupon.discountType ? "coupon" : "automatic"),

    priority: coupon.priority?.toString() || "1",

    // Bug 7 fix: new Firestore fields for admin-controlled display
    icon: coupon.icon || "",
    badge: coupon.badge || "",

    storeIds: arrayify(coupon.storeIds),
    applicableCities: arrayify(coupon.applicableCities),
    applicableDeliveryZones: arrayify(coupon.applicableDeliveryZones),
    startDate: coupon.startDate || "",

    // Bug 8 fix: endDate and expiryDate are both tracked independently
    endDate: coupon.endDate || coupon.expiryDate || "",
    expiryDate: coupon.expiryDate || coupon.endDate || "",

    qualification: {
      type: qType,
      minimumOrder: (q.minimumOrder ?? coupon.minimumOrder)?.toString() || "",

      // Bug 4 fix: read from nested qualification first, fall back to root xValue
      quantity: (q.buyQuantity ?? q.quantity ?? coupon.xValue)?.toString() || "",

      // Bug 4 fix: read buyItems from nested qualification first, fall back to root buyItems
      productIds: parseLegacyItems(q.buyItems || q.productIds || coupon.buyItems),

      categoryIds: arrayify(q.categoryIds || coupon.categoryIds),
      brandIds: arrayify(q.brandIds || coupon.brandIds),
      comboItems: q.comboItems || coupon.comboItems || [],
      entities: qualEntities,
      customRule: q.customRule || coupon.customRule || "",
      repeatMode: normalizeRepeatMode(q.repeat || q.repeatMode || coupon.repeatMode, coupon.usage === "single" ? "NONE" : "UNLIMITED"),
      repeatLimit: (q.repeatLimit ?? coupon.repeatLimit)?.toString() || "",
    },

    reward: {
      type: rType,

      // Bug 3 fix: read discountAmount from nested reward first, fall back to root discountAmount
      amount: (r.discountAmount ?? coupon.discountAmount)?.toString() || "",

      // Bug 3 fix: read discountPercent from nested reward first, fall back to root discountPercent
      percent: (r.discountPercent ?? coupon.discountPercent)?.toString() || "",

      maximumDiscount: (r.maximumDiscount ?? coupon.maximumDiscount)?.toString() || "",
      targetPrice: (r.targetPrice ?? coupon.targetPrice)?.toString() || "",

      // Bug 3 fix: read rewardItems from nested reward first, fall back to root freeItems
      productIds: parseLegacyItems(r.rewardItems || r.productIds || coupon.freeItems),

      // Bug 3 fix: read rewardQuantity from nested reward first, fall back to root yValue
      quantity: (r.rewardQuantity ?? r.quantity ?? coupon.yValue)?.toString() || "",

      points: (r.points ?? coupon.rewardPoints)?.toString() || "",
      walletCredit: (r.walletCredit ?? coupon.walletCredit)?.toString() || "",

      // Dynamic reward target fields (new — default to 'product' for old documents)
      targetType: r.targetType ? r.targetType.toLowerCase() : "product",
      rewardCategory: r.rewardCategory || "",
      rewardVariantId: r.rewardVariantId || "",
      rewardBrand: r.rewardBrand || "",
      rewardCombo: r.rewardCombo || [],
      entities: rewardEntities,
    },

    policy: {
      selection: pol.rewardPolicy || pol.selection || coupon.rewardPolicy || coupon.selection || "fixedProduct",
      maxRewardQuantity: (pol.maxRewardQuantity ?? coupon.maxRewardQuantity)?.toString() || "",
      rewardRepeatMode: normalizeRepeatMode(
        pol.rewardRepeatMode || pol.repeat || coupon.rewardRepeatMode || coupon.repeat,
        "UNLIMITED"
      ),
      rewardLimit: (pol.rewardLimit ?? pol.repeatLimit ?? coupon.rewardLimit ?? coupon.repeatLimit)?.toString() || "",
    },

    restrictions: {
      firstOrderOnly: res.firstOrderOnly || false,
      specificCustomers: res.specificCustomers || [],
      customerGroups: res.customerGroups || [],
      storeIds: res.storeIds || [],
      deliveryTypes: res.deliveryTypes || [],
      maximumUsage: res.maximumUsage?.toString() || "",
      maximumUsagePerCustomer: res.maximumUsagePerCustomer?.toString() || "",
      allowStacking: res.allowStacking ?? true,
    },
  };

  return promotion;
};
